'use client';

import { useAuth } from '@/hooks/useAuth';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { collection, doc, onSnapshot, orderBy, query } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { sanitizeFirestoreData, cn } from '@/lib/utils';
import { CityMapConfig, CityMapPin, CityMapPinCategory, CitySecondaryMap } from '@/lib/types';
import {
  createCityMapPin,
  deleteCityMapPin,
  setCityMapImage,
  setCityMapMeta,
  updateCityMapPin,
  updateCityMapPinPosition,
  createSecondaryMap,
  updateSecondaryMap,
  deleteSecondaryMap,
} from '@/lib/actions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Loader2,
  MapPin as MapPinIcon,
  Plus,
  Upload,
  X,
  Trash2,
  Church,
  Landmark,
  Building2,
  LandPlot,
  ImagePlus,
  Map as MapIcon,
  Pencil,
  Save,
} from 'lucide-react';

const CATEGORY_OPTIONS: { value: CityMapPinCategory; label: string; icon: React.ElementType }[] = [
  { value: 'iglesia', label: 'Iglesia', icon: Church },
  { value: 'catedral', label: 'Catedral', icon: Landmark },
  { value: 'zona', label: 'Zona', icon: LandPlot },
  { value: 'edificio', label: 'Edificio', icon: Building2 },
];

function getCategoryIcon(category: CityMapPinCategory) {
  return CATEGORY_OPTIONS.find((c) => c.value === category)?.icon || MapPinIcon;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

interface PinFormProps {
  mode: 'new' | 'edit';
  initialPin?: CityMapPin | null;
  onSuccess: () => void;
  onCancel: () => void;
  onDelete?: () => void;
  position?: { x: number; y: number };
  mapId?: string;
}

function PinForm({ mode, initialPin, onSuccess, onCancel, onDelete, position, mapId = 'main' }: PinFormProps) {
  const { toast } = useToast();
  const [name, setName] = useState(initialPin?.name || '');
  const [category, setCategory] = useState<CityMapPinCategory>(initialPin?.category || 'iglesia');
  const [description, setDescription] = useState(initialPin?.description || '');
  const [existingImages, setExistingImages] = useState<string[]>(initialPin?.images || []);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [newPreviews, setNewPreviews] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const totalImages = existingImages.length + newFiles.length;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (totalImages >= 2) {
      toast({ title: 'Límite alcanzado', description: 'Solo se permiten hasta 2 imágenes por chincheta.', variant: 'destructive' });
      return;
    }
    setNewFiles((prev) => [...prev, file]);
    setNewPreviews((prev) => [...prev, URL.createObjectURL(file)]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeExistingImage = (url: string) => {
    setExistingImages((prev) => prev.filter((img) => img !== url));
  };

  const removeNewFile = (index: number) => {
    setNewFiles((prev) => prev.filter((_, i) => i !== index));
    setNewPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast({ title: 'Falta el nombre', description: 'Indica el nombre del lugar.', variant: 'destructive' });
      return;
    }
    setIsSubmitting(true);

    const data = { name: name.trim(), category, description: description.trim() };
    let result;
    if (mode === 'new' && position) {
      result = await createCityMapPin(data, position.x, position.y, newFiles, mapId);
    } else if (initialPin) {
      result = await updateCityMapPin(initialPin.id, data, existingImages, newFiles);
    }

    if (result?.success) {
      toast({ title: 'Éxito', description: result.message });
      onSuccess();
    } else {
      toast({ title: 'Error', description: result?.message, variant: 'destructive' });
    }
    setIsSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="pin-name">Nombre del lugar</Label>
        <Input id="pin-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej: Catedral de Jartiland" />
      </div>

      <div className="space-y-2">
        <Label htmlFor="pin-category">Categoría</Label>
        <Select value={category} onValueChange={(v) => setCategory(v as CityMapPinCategory)}>
          <SelectTrigger id="pin-category">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CATEGORY_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                <div className="flex items-center gap-2">
                  <opt.icon className="h-4 w-4" />
                  {opt.label}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="pin-description">Descripción</Label>
        <Textarea
          id="pin-description"
          rows={4}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Describe este lugar..."
        />
      </div>

      <div className="space-y-2">
        <Label>Imágenes (máximo 2, opcional)</Label>
        <div className="grid grid-cols-2 gap-3">
          {existingImages.map((url) => (
            <div key={url} className="group relative aspect-video overflow-hidden rounded-lg border">
              <img src={url} alt="" className="h-full w-full object-cover" />
              <button
                type="button"
                onClick={() => removeExistingImage(url)}
                className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/70 text-white opacity-0 transition-opacity group-hover:opacity-100"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          {newPreviews.map((url, i) => (
            <div key={url} className="group relative aspect-video overflow-hidden rounded-lg border border-primary/40">
              <img src={url} alt="" className="h-full w-full object-cover" />
              <button
                type="button"
                onClick={() => removeNewFile(i)}
                className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/70 text-white opacity-0 transition-opacity group-hover:opacity-100"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          {totalImages < 2 && (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex aspect-video flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-muted-foreground/30 text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary"
            >
              <ImagePlus className="h-5 w-5" />
              <span className="text-xs">Añadir imagen</span>
            </button>
          )}
        </div>
        <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
      </div>

      <DialogFooter className="gap-2 sm:justify-between">
        <div>
          {mode === 'edit' && onDelete && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button type="button" variant="ghost" className="text-destructive hover:text-destructive">
                  <Trash2 className="h-4 w-4" />
                  Eliminar
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>¿Eliminar esta chincheta?</AlertDialogTitle>
                  <AlertDialogDescription>Esta acción es permanente y no se puede deshacer.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={onDelete} className="bg-destructive hover:bg-destructive/80">
                    Confirmar Eliminación
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancelar
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {mode === 'new' ? 'Añadir Chincheta' : 'Guardar Cambios'}
          </Button>
        </div>
      </DialogFooter>
    </form>
  );
}

export default function AdminMapaPage() {
  const { user, userData, loading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [mapConfig, setMapConfig] = useState<CityMapConfig | null>(null);
  const [secondaryMaps, setSecondaryMaps] = useState<CitySecondaryMap[]>([]);
  const [allPins, setAllPins] = useState<CityMapPin[]>([]);
  const [activeMapId, setActiveMapId] = useState<string>('main'); // 'main' | id secundario
  const [isLoadingMap, setIsLoadingMap] = useState(true);
  const [isUploadingMap, setIsUploadingMap] = useState(false);
  const [isAddMode, setIsAddMode] = useState(false);
  const [pendingPosition, setPendingPosition] = useState<{ x: number; y: number } | null>(null);
  const [editingPin, setEditingPin] = useState<CityMapPin | null>(null);
  const [isNewDialogOpen, setIsNewDialogOpen] = useState(false);
  const [dragPositions, setDragPositions] = useState<Record<string, { x: number; y: number }>>({});
  // Gestión de metadatos y mapas secundarios
  const [metaOpen, setMetaOpen] = useState(false);
  const [newMapOpen, setNewMapOpen] = useState(false);

  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapFileInputRef = useRef<HTMLInputElement>(null);
  const dragState = useRef<{ id: string; startX: number; startY: number; moved: boolean } | null>(null);

  useEffect(() => {
    if (!loading && userData?.role !== 'admin') {
      navigate('/jartinet', { replace: true });
    }
  }, [user, userData, loading, navigate]);

  useEffect(() => {
    if (loading || userData?.role !== 'admin') return;

    const unsubConfig = onSnapshot(doc(db, 'city_map_config', 'main'), (snap) => {
      setMapConfig(snap.exists() ? (sanitizeFirestoreData({ id: snap.id, ...snap.data() }) as CityMapConfig) : null);
      setIsLoadingMap(false);
    });

    const unsubSecondary = onSnapshot(collection(db, 'city_maps'), (snapshot) => {
      setSecondaryMaps(snapshot.docs.map((d) => sanitizeFirestoreData({ id: d.id, ...d.data() })) as CitySecondaryMap[]);
    });

    const q = query(collection(db, 'city_map_pins'), orderBy('createdAt', 'asc'));
    const unsubPins = onSnapshot(q, (snapshot) => {
      setAllPins(snapshot.docs.map((d) => sanitizeFirestoreData({ id: d.id, ...d.data() })) as CityMapPin[]);
    });

    return () => {
      unsubConfig();
      unsubSecondary();
      unsubPins();
    };
  }, [loading, userData]);

  const isMain = activeMapId === 'main';
  const activeSecondary = isMain ? null : secondaryMaps.find((m) => m.id === activeMapId) || null;
  const activeImageUrl = isMain ? mapConfig?.mapImageUrl : activeSecondary?.mapImageUrl;
  const activeName = isMain ? (mapConfig?.name || 'Mapa principal') : (activeSecondary?.name || 'Mapa secundario');
  const activeDescription = isMain ? (mapConfig?.description || '') : (activeSecondary?.description || '');
  const pins = allPins.filter((p) => (p.mapId || 'main') === activeMapId);

  const handleMapFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploadingMap(true);
    const result = isMain
      ? await setCityMapImage(file)
      : await updateSecondaryMap(activeMapId, {}, file);
    if (result.success) {
      toast({ title: 'Éxito', description: result.message });
    } else {
      toast({ title: 'Error', description: result.message, variant: 'destructive' });
    }
    setIsUploadingMap(false);
    if (mapFileInputRef.current) mapFileInputRef.current.value = '';
  };

  const handleDeleteSecondary = async () => {
    if (isMain) return;
    const result = await deleteSecondaryMap(activeMapId);
    if (result.success) {
      toast({ title: 'Éxito', description: result.message });
      setActiveMapId('main');
    } else {
      toast({ title: 'Error', description: result.message, variant: 'destructive' });
    }
  };

  const handleMapClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isAddMode || !mapContainerRef.current) return;
    const rect = mapContainerRef.current.getBoundingClientRect();
    const x = clamp(((e.clientX - rect.left) / rect.width) * 100, 0, 100);
    const y = clamp(((e.clientY - rect.top) / rect.height) * 100, 0, 100);
    setPendingPosition({ x, y });
    setIsNewDialogOpen(true);
    setIsAddMode(false);
  };

  const handlePointerDown = (e: React.PointerEvent, pin: CityMapPin) => {
    e.stopPropagation();
    if (isAddMode) return;
    (e.target as Element).setPointerCapture(e.pointerId);
    dragState.current = { id: pin.id, startX: e.clientX, startY: e.clientY, moved: false };
  };

  const handlePointerMove = (e: React.PointerEvent, pin: CityMapPin) => {
    if (!dragState.current || dragState.current.id !== pin.id || !mapContainerRef.current) return;
    const dx = Math.abs(e.clientX - dragState.current.startX);
    const dy = Math.abs(e.clientY - dragState.current.startY);
    if (dx > 4 || dy > 4) dragState.current.moved = true;
    if (!dragState.current.moved) return;

    const rect = mapContainerRef.current.getBoundingClientRect();
    const x = clamp(((e.clientX - rect.left) / rect.width) * 100, 0, 100);
    const y = clamp(((e.clientY - rect.top) / rect.height) * 100, 0, 100);
    setDragPositions((prev) => ({ ...prev, [pin.id]: { x, y } }));
  };

  const handlePointerUp = async (e: React.PointerEvent, pin: CityMapPin) => {
    if (!dragState.current || dragState.current.id !== pin.id) return;
    const wasDrag = dragState.current.moved;
    dragState.current = null;

    if (wasDrag) {
      const pos = dragPositions[pin.id];
      if (pos) {
        await updateCityMapPinPosition(pin.id, pos.x, pos.y);
      }
      setDragPositions((prev) => {
        const next = { ...prev };
        delete next[pin.id];
        return next;
      });
    } else {
      setEditingPin(pin);
    }
  };

  const handleDeletePin = async (pin: CityMapPin) => {
    const result = await deleteCityMapPin(pin.id, pin.images);
    if (result.success) {
      toast({ title: 'Éxito', description: result.message });
      setEditingPin(null);
    } else {
      toast({ title: 'Error', description: result.error, variant: 'destructive' });
    }
  };

  if (loading || userData?.role !== 'admin') {
    return (
      <div className="flex h-[calc(100vh-10rem)] w-full items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        <p className="ml-4">Verificando permisos...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-headline text-5xl font-bold text-primary">Gestionar Mapas</h1>
        <p className="mt-4 text-lg text-muted-foreground">
          Gestiona el mapa principal y los mapas secundarios. Sube imágenes y coloca chinchetas en cada uno.
        </p>
      </div>

      {/* Selector de mapas */}
      <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-white/8 bg-card/40 p-2">
        <button
          onClick={() => { setActiveMapId('main'); setIsAddMode(false); }}
          className={cn(
            'flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium transition-colors',
            isMain ? 'bg-primary text-primary-foreground' : 'hover:bg-white/5'
          )}
        >
          <MapIcon className="h-4 w-4" /> {mapConfig?.name || 'Mapa principal'}
          <span className="rounded-full bg-black/20 px-1.5 text-[0.65rem]">Principal</span>
        </button>
        {secondaryMaps.map((m) => (
          <button
            key={m.id}
            onClick={() => { setActiveMapId(m.id); setIsAddMode(false); }}
            className={cn(
              'flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium transition-colors',
              activeMapId === m.id ? 'bg-primary text-primary-foreground' : 'hover:bg-white/5'
            )}
          >
            <MapIcon className="h-4 w-4" /> {m.name || 'Sin nombre'}
          </button>
        ))}
        <Button variant="outline" size="sm" className="ml-auto gap-1" onClick={() => setNewMapOpen(true)}>
          <Plus className="h-4 w-4" /> Mapa secundario
        </Button>
      </div>

      {/* Cabecera del mapa activo */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-white/8 bg-card/30 p-4">
        <div className="min-w-0">
          <h2 className="text-xl font-bold truncate">{activeName}</h2>
          {activeDescription && <p className="text-sm text-muted-foreground line-clamp-2">{activeDescription}</p>}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="gap-1" onClick={() => setMetaOpen(true)}>
            <Pencil className="h-4 w-4" /> Editar nombre/descripción
          </Button>
          {!isMain && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-1 text-destructive hover:text-destructive"><Trash2 className="h-4 w-4" /> Eliminar mapa</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>¿Eliminar este mapa secundario?</AlertDialogTitle>
                  <AlertDialogDescription>Se eliminará el mapa y todas sus chinchetas. Es permanente.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDeleteSecondary} className="bg-destructive hover:bg-destructive/80">Eliminar</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      {isLoadingMap ? (
        <div className="flex items-center justify-center p-12">
          <Loader2 className="h-10 w-10 animate-spin text-primary" />
        </div>
      ) : !activeImageUrl ? (
        <div className="flex flex-col items-center justify-center gap-4 rounded-lg border-2 border-dashed py-20 text-center">
          <Upload className="h-12 w-12 text-muted-foreground" />
          <div>
            <h3 className="text-lg font-semibold">Todavía no hay un mapa cargado</h3>
            <p className="mt-1 text-sm text-muted-foreground">Sube una imagen desde tus archivos para empezar a colocar chinchetas.</p>
          </div>
          <Button onClick={() => mapFileInputRef.current?.click()} disabled={isUploadingMap} size="lg">
            {isUploadingMap ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            Cargar mapa
          </Button>
          <input ref={mapFileInputRef} type="file" accept="image/*" className="hidden" onChange={handleMapFileSelected} />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant={isAddMode ? 'default' : 'outline'}
              onClick={() => setIsAddMode((v) => !v)}
            >
              <Plus className="h-4 w-4" />
              {isAddMode ? 'Haz clic en el mapa para colocar la chincheta...' : 'Añadir chincheta'}
            </Button>
            <Button variant="outline" onClick={() => mapFileInputRef.current?.click()} disabled={isUploadingMap}>
              {isUploadingMap ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              Cambiar mapa
            </Button>
            <input ref={mapFileInputRef} type="file" accept="image/*" className="hidden" onChange={handleMapFileSelected} />
            <p className="text-xs text-muted-foreground">
              {pins.length} chincheta{pins.length !== 1 ? 's' : ''} colocada{pins.length !== 1 ? 's' : ''}. Arrastra una chincheta para reposicionarla, o haz clic en ella para editarla.
            </p>
          </div>

          <div
            ref={mapContainerRef}
            onClick={handleMapClick}
            className={cn(
              'relative w-full select-none overflow-hidden rounded-lg border',
              isAddMode ? 'cursor-crosshair' : 'cursor-default'
            )}
          >
            <img src={activeImageUrl} alt="Mapa de la ciudad" className="block w-full" draggable={false} />

            {pins.map((pin) => {
              const pos = dragPositions[pin.id] || { x: pin.x, y: pin.y };
              const Icon = getCategoryIcon(pin.category);
              return (
                <div
                  key={pin.id}
                  onPointerDown={(e) => handlePointerDown(e, pin)}
                  onPointerMove={(e) => handlePointerMove(e, pin)}
                  onPointerUp={(e) => handlePointerUp(e, pin)}
                  className="absolute z-10 flex h-9 w-9 -translate-x-1/2 -translate-y-full cursor-grab touch-none items-center justify-center rounded-full border-2 border-white bg-primary text-primary-foreground shadow-lg transition-transform hover:scale-110 active:cursor-grabbing"
                  style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
                  title={pin.name}
                >
                  <Icon className="h-4 w-4" />
                </div>
              );
            })}

            {pendingPosition && isNewDialogOpen && (
              <div
                className="absolute z-10 flex h-9 w-9 -translate-x-1/2 -translate-y-full items-center justify-center rounded-full border-2 border-dashed border-white bg-primary/60 text-primary-foreground"
                style={{ left: `${pendingPosition.x}%`, top: `${pendingPosition.y}%` }}
              >
                <MapPinIcon className="h-4 w-4" />
              </div>
            )}
          </div>
        </div>
      )}

      {/* New pin dialog */}
      <Dialog
        open={isNewDialogOpen}
        onOpenChange={(open) => {
          setIsNewDialogOpen(open);
          if (!open) setPendingPosition(null);
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Nueva Chincheta</DialogTitle>
            <DialogDescription>Rellena la información del lugar que quieres destacar en el mapa.</DialogDescription>
          </DialogHeader>
          {pendingPosition && (
            <PinForm
              mode="new"
              position={pendingPosition}
              mapId={activeMapId}
              onSuccess={() => {
                setIsNewDialogOpen(false);
                setPendingPosition(null);
              }}
              onCancel={() => {
                setIsNewDialogOpen(false);
                setPendingPosition(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Edit pin dialog */}
      <Dialog open={!!editingPin} onOpenChange={(open) => !open && setEditingPin(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Editar Chincheta</DialogTitle>
            <DialogDescription>Modifica la información o elimina esta chincheta.</DialogDescription>
          </DialogHeader>
          {editingPin && (
            <PinForm
              mode="edit"
              initialPin={editingPin}
              onSuccess={() => setEditingPin(null)}
              onCancel={() => setEditingPin(null)}
              onDelete={() => handleDeletePin(editingPin)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Editar nombre/descripción del mapa activo */}
      <MapMetaDialog
        open={metaOpen}
        onClose={() => setMetaOpen(false)}
        isMain={isMain}
        mapId={activeMapId}
        initialName={isMain ? (mapConfig?.name || '') : (activeSecondary?.name || '')}
        initialDescription={activeDescription}
      />

      {/* Crear mapa secundario */}
      <NewSecondaryMapDialog
        open={newMapOpen}
        onClose={() => setNewMapOpen(false)}
        onCreated={(id) => { setNewMapOpen(false); setActiveMapId(id); }}
      />
    </div>
  );
}

function MapMetaDialog({ open, onClose, isMain, mapId, initialName, initialDescription }: {
  open: boolean; onClose: () => void; isMain: boolean; mapId: string; initialName: string; initialDescription: string;
}) {
  const { toast } = useToast();
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setName(initialName); setDescription(initialDescription); } }, [open, initialName, initialDescription]);

  const handleSave = async () => {
    setSaving(true);
    const result = isMain
      ? await setCityMapMeta(name.trim(), description.trim())
      : await updateSecondaryMap(mapId, { name: name.trim(), description: description.trim() });
    if (result.success) { toast({ title: 'Éxito', description: result.message }); onClose(); }
    else toast({ title: 'Error', description: result.message, variant: 'destructive' });
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Nombre y descripción del mapa</DialogTitle>
          <DialogDescription>{isMain ? 'Este es el mapa principal (el que aparece en los libritos).' : 'Mapa secundario.'}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2"><Label>Nombre</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej: Jartiland centro" /></div>
          <div className="space-y-2"><Label>Descripción</Label><Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Describe este mapa..." /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Guardar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewSecondaryMapDialog({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: (id: string) => void }) {
  const { toast } = useToast();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { if (open) { setName(''); setDescription(''); setFile(null); setPreview(''); } }, [open]);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const handleCreate = async () => {
    if (!name.trim()) { toast({ title: 'Falta el nombre', variant: 'destructive' }); return; }
    setSaving(true);
    const result = await createSecondaryMap(name.trim(), description.trim(), file);
    if (result.success && result.id) { toast({ title: 'Éxito', description: result.message }); onCreated(result.id); }
    else toast({ title: 'Error', description: result.message, variant: 'destructive' });
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Nuevo mapa secundario</DialogTitle>
          <DialogDescription>Ponle nombre y descripción, y carga la imagen. Luego podrás colocar chinchetas.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2"><Label>Nombre</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej: Mapa de los Reales Alcázares" /></div>
          <div className="space-y-2"><Label>Descripción</Label><Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Describe este mapa..." /></div>
          <div className="space-y-2">
            <Label>Imagen del mapa</Label>
            {preview ? (
              <div className="relative overflow-hidden rounded-lg border">
                <img src={preview} alt="" className="block w-full" />
                <button type="button" onClick={() => { setFile(null); setPreview(''); }} className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-black/70 text-white"><X className="h-4 w-4" /></button>
              </div>
            ) : (
              <button type="button" onClick={() => inputRef.current?.click()} className="flex w-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-muted-foreground/30 py-10 text-muted-foreground hover:border-primary/50 hover:text-primary">
                <ImagePlus className="h-6 w-6" /> <span className="text-sm">Cargar imagen</span>
              </button>
            )}
            <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleCreate} disabled={saving}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Crear mapa</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
